import { createRouter, createWebHistory } from 'vue-router';

import { frontendURL } from '../helper/URLHelper';
import dashboard from './dashboard/dashboard.routes';
import store from 'dashboard/store';
import { validateLoggedInRoutes } from '../helper/routeHelpers';
import { isOnOnboardingView } from 'v3/helpers/RouteHelper';
import AnalyticsHelper from '../helper/AnalyticsHelper';
// FORK_PATCH_BEGIN: account-public-id — validate browser account route values
import {
  findAccountByIdentifier,
  isValidAccountIdentifier,
} from '../helper/accountIdentifier';
// FORK_PATCH_END: account-public-id
// FORK_PATCH_BEGIN: integrations-feature-gate
import { accountFeatureRouteRedirect } from 'custom/dashboard/helper/accountFeatureRoute';
// FORK_PATCH_END: integrations-feature-gate
// FORK_PATCH_BEGIN: registration-pending-gate
import { ensureAccountRecord } from 'custom/dashboard/helper/registrationPending';
// FORK_PATCH_END: registration-pending-gate
// FORK_PATCH_BEGIN: empty-inbox-usage-guide-landing
import {
  isAccountLandingNavigation,
  isDashboardLandingRoute,
  resolveDefaultAccountLandingPath,
} from 'custom/dashboard/helper/defaultAccountLanding';
// FORK_PATCH_END: empty-inbox-usage-guide-landing

const ONBOARDING_STEPS = ['account_details', 'enrichment'];
const routes = [...dashboard.routes];

export const router = createRouter({ history: createWebHistory(), routes });

export const validateAuthenticateRoutePermission = async (
  to,
  next,
  from = {}
) => {
  const { isLoggedIn, getCurrentUser: user } = store.getters;

  if (!isLoggedIn) {
    window.location.assign('/app/login');
    return '';
  }

  const { accounts = [], account_id: accountId } = user;

  if (!accounts.length) {
    if (to.name === 'no_accounts') {
      return next();
    }
    return next(frontendURL('no-accounts'));
  }

  // FORK_PATCH_BEGIN: account-public-id — reject numeric and malformed routes
  const routeParam = to.params?.accountId;
  if (routeParam !== undefined && !isValidAccountIdentifier(routeParam)) {
    window.location.assign(to.fullPath || to.path);
    return '';
  }

  const routeAccountId = routeParam || accountId;
  const userAccount = findAccountByIdentifier(accounts, routeAccountId);
  if (!userAccount && routeParam !== undefined) {
    window.location.assign(to.fullPath || to.path);
    return '';
  }
  // FORK_PATCH_END: account-public-id
  const isAdmin = userAccount?.role === 'administrator';
  const isActive = userAccount?.status === 'active';
  // FORK_PATCH: registration-pending-gate — pending merchants still complete
  // onboarding first; WhatsApp gate shows only after onboarding_step is cleared.
  // Ensure account.settings (incl. registration_status) is in the store for App.vue.
  await ensureAccountRecord(store, routeAccountId);
  const needsOnboarding =
    ONBOARDING_STEPS.includes(userAccount?.onboarding_step) &&
    isAdmin &&
    isActive;

  // FORK_PATCH_BEGIN: empty-inbox-usage-guide-landing
  const resolveHomePath = async () => {
    if (needsOnboarding) return 'onboarding';
    return resolveDefaultAccountLandingPath(store, {
      isAdministrator: isAdmin,
    });
  };
  // FORK_PATCH_END: empty-inbox-usage-guide-landing

  if (to.name === 'no_accounts' || !to.name) {
    // FORK_PATCH_BEGIN: empty-inbox-usage-guide-landing
    const target = await resolveHomePath();
    return next(frontendURL(`accounts/${routeAccountId}/${target}`));
    // FORK_PATCH_END: empty-inbox-usage-guide-landing
  }

  if (needsOnboarding && !isOnOnboardingView(to)) {
    return next(frontendURL(`accounts/${routeAccountId}/onboarding`));
  }
  if (!needsOnboarding && isOnOnboardingView(to)) {
    // FORK_PATCH_BEGIN: empty-inbox-usage-guide-landing
    // After onboarding, prefer usage-guide (no inbox) or Settings → Inboxes.
    const target = await resolveHomePath();
    return next(frontendURL(`accounts/${routeAccountId}/${target}`));
    // FORK_PATCH_END: empty-inbox-usage-guide-landing
  }

  // FORK_PATCH_BEGIN: empty-inbox-usage-guide-landing
  // Login and account switchers land on /dashboard; admins go to Inboxes
  // (or usage-guide when no inbox exists yet). Do not hijack in-app
  // clicks on sidebar "所有会话" (same route name: home).
  if (
    to.name === 'home' &&
    isAdmin &&
    isActive &&
    !needsOnboarding &&
    isAccountLandingNavigation(to, from)
  ) {
    const target = await resolveDefaultAccountLandingPath(store, {
      isAdministrator: true,
    });
    if (target !== 'dashboard') {
      return next(frontendURL(`accounts/${routeAccountId}/${target}`));
    }
  }
  // FORK_PATCH_END: empty-inbox-usage-guide-landing

  const nextRoute = validateLoggedInRoutes(to, store.getters.getCurrentUser);
  if (nextRoute) {
    // FORK_PATCH_BEGIN: empty-inbox-usage-guide-landing
    if (isAdmin && isDashboardLandingRoute(nextRoute)) {
      const target = await resolveDefaultAccountLandingPath(store, {
        isAdministrator: true,
      });
      return next(frontendURL(`accounts/${routeAccountId}/${target}`));
    }
    // FORK_PATCH_END: empty-inbox-usage-guide-landing
    return next(frontendURL(nextRoute));
  }

  // FORK_PATCH: load account features before route gate on cold navigation (e.g. refresh on /settings/inboxes/list).
  if (routeAccountId && to.meta?.featureFlag) {
    const account = store.getters['accounts/getAccount'](routeAccountId);
    if (!account?.features || Object.keys(account.features).length === 0) {
      await store.dispatch('accounts/get', { silent: true });
    }
  }

  // FORK_PATCH_BEGIN: integrations-feature-gate — honor account feature flags on routes
  const featureRedirect = accountFeatureRouteRedirect(
    to,
    routeAccountId,
    store.getters['accounts/isFeatureEnabledonAccount']
  );
  if (featureRedirect) {
    // FORK_PATCH_BEGIN: empty-inbox-usage-guide-landing
    if (isAdmin && isDashboardLandingRoute(featureRedirect)) {
      const target = await resolveDefaultAccountLandingPath(store, {
        isAdministrator: true,
      });
      return next(frontendURL(`accounts/${routeAccountId}/${target}`));
    }
    // FORK_PATCH_END: empty-inbox-usage-guide-landing
    return next(frontendURL(featureRedirect));
  }
  // FORK_PATCH_END: integrations-feature-gate

  return next();
};

export const initalizeRouter = () => {
  const userAuthentication = store.dispatch('setUser');

  router.beforeEach(async (to, from, next) => {
    AnalyticsHelper.page(to.name || '', {
      path: to.path,
      name: to.name,
    });

    await userAuthentication;
    await validateAuthenticateRoutePermission(to, next, from);
  });
};

export default router;
